import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randProductName,
  randNumber,
  randChanceBoolean,
  randRecentDate,
} from '@ngneat/falso'

import { ITEM_TYPE_CODES } from '@/composables/api/NavigationSearch.js'
import type { SchemaAttributes } from '../../types.js'
import type { MirageServer, MockServer } from '../server.js'

type CatalogItemData = SchemaAttributes<'Item'>

/**
 * Trigger mock websocket item-update-status messages via the Nitro dev server.
 * Simulates the real backend's Celery → Socketly flow for bulk operations.
 */
function triggerMockItemUpdateStatus(ids: number[]) {
  if (ids.length === 0) {
    return
  }
  // Use the dev server origin (same as current page) — Mirage's passthrough
  // will let this reach the Nitro server since it's not under the API base URL
  void fetch('/api/mock/item-update-status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  }).catch(() => {
    // Silently ignore — mock websocket server may not be running
  })
}

export const model = Model as ModelDefinition<CatalogItemData>

export const factory = Factory.extend<CatalogItemData>({
  name() {
    return randProductName()
  },
  type_code() {
    return randNumber({ min: 1, max: 6 }) as 1 | 2 | 3 | 4 | 5 | 6
  },
  code() {
    return `ITEM-${randNumber({ min: 1000, max: 9999 })}`
  },
  archived() {
    return randChanceBoolean({ chanceTrue: 0.2 })
  },
  can_delete() {
    return true
  },
  is_stock_item() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  barcode(i: number) {
    return i % 3 === 0
      ? `${randNumber({ min: 1000000000000, max: 9999999999999 })}`
      : null
  },
  print_name(i: number) {
    return i % 4 === 0 ? `${randProductName()} (print)` : null
  },
})

export function seeds(server: MockServer) {
  server.createList('catalogItem', 50)
}

export function routes(server: MockServer) {
  // GET /catalog/items/v1 - Get item options for dropdown
  server.get(
    '/catalog/items/v1',
    function (this: MirageServer, schema, request) {
      // Ensure related entities are seeded (they use lazy seeding)
      if (schema.all('itemCategory').length === 0) {
        server.createList('itemCategory', 20)
      }
      if (schema.all('itemSubgroup').length === 0) {
        server.createList('itemSubgroup', 8)
      }
      if (schema.all('wholesaler').length === 0) {
        server.createList('wholesaler', 5)
      }

      const allItems = schema.all('catalogItem')
      const allCategories = schema.all('itemCategory').models
      const allSubgroups = schema.all('itemSubgroup').models
      const allWholesalers = schema.all('wholesaler').models
      const qp = request.queryParams

      // Helper to parse comma-separated filter values
      const filterValues = (key: string): string[] => {
        const raw = qp[`filter[${key}]`]
        if (!raw) {
          return []
        }
        return String(raw)
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean)
      }

      // Maps type_code to item_type enum matching the real API schema
      const typeCodeToItemType: Record<number, string> = {
        [ITEM_TYPE_CODES.PROCEDURE]: 'PROCEDURE',
        [ITEM_TYPE_CODES.MEDICINE]: 'MEDICINE',
        [ITEM_TYPE_CODES.SUPPLY]: 'SUPPLY',
        [ITEM_TYPE_CODES.FOOD]: 'FOOD',
        [ITEM_TYPE_CODES.LABORATORY_ANALYSIS]: 'LABORATORY_ANALYSIS',
        [ITEM_TYPE_CODES.LABORATORY_ANALYSIS_PANEL]:
          'LABORATORY_ANALYSIS_PANEL',
      }

      // Build enriched items with computed fields for filtering
      let items = allItems.models.map((item) => {
        const id = Number(item.id)
        const itemType = typeCodeToItemType[item.attrs.type_code] || 'PROCEDURE'
        const categories = allCategories
          .filter((_, i) => (id + i) % 5 === 0)
          .slice(0, 3)
        // Assign a subgroup based on item ID
        const subgroup = allSubgroups[id % allSubgroups.length] ?? null
        // Assign a wholesaler based on item ID
        const wholesaler = allWholesalers[id % allWholesalers.length] ?? null
        return { item, itemType, categories, subgroup, wholesaler }
      })

      // Search filter
      const search = qp['filter[search]']
      if (search) {
        const q = String(search).toLowerCase()
        items = items.filter((r) =>
          r.item.attrs.name?.toLowerCase().includes(q),
        )
      }

      // Archived filter
      const archived = qp['filter[archived]']
      if (archived === 'true') {
        items = items.filter((r) => r.item.attrs.archived)
      } else if (archived === 'false') {
        items = items.filter((r) => !r.item.attrs.archived)
      }

      // Item type filter
      const itemTypes = filterValues('item_type')
      if (itemTypes.length > 0) {
        items = items.filter((r) => itemTypes.includes(r.itemType))
      }

      // Item category filter
      const categoryIds = filterValues('item_category_id')
      if (categoryIds.length > 0) {
        items = items.filter((r) =>
          r.categories.some((c) => categoryIds.includes(String(c.id))),
        )
      }

      // Subgroup filter
      const subgroupIds = filterValues('subgroup_id')
      if (subgroupIds.length > 0) {
        items = items.filter(
          (r) => r.subgroup && subgroupIds.includes(String(r.subgroup.id)),
        )
      }

      // Wholesaler filter
      const wholesalerIds = filterValues('wholesaler_id')
      if (wholesalerIds.length > 0) {
        items = items.filter(
          (r) =>
            r.wholesaler && wholesalerIds.includes(String(r.wholesaler.id)),
        )
      }

      // Pagination
      const pageNumber = parseInt(String(qp['page[number]'] ?? '1'))
      const pageSize = parseInt(String(qp['page[size]'] ?? '25'))
      const totalCount = items.length
      const start = (pageNumber - 1) * pageSize
      const paged = items.slice(start, start + pageSize)

      return {
        data: paged.map(({ item, itemType, categories, subgroup }) => ({
          id: item.id,
          type: 'Item',
          attributes: {
            name: item.attrs.name,
            type_code: item.attrs.type_code,
            item_type: itemType,
            code: item.attrs.code,
            barcode: item.attrs.barcode,
            print_name: item.attrs.print_name,
            archived: item.attrs.archived ?? false,
            can_delete: item.attrs.can_delete ?? true,
            is_stock_item: item.attrs.is_stock_item ?? false,
            modified: randRecentDate().toISOString(),
            subgroup: subgroup
              ? { id: subgroup.id, name: subgroup.attrs.title }
              : null,
            item_categories: categories.map((c) => ({
              id: c.id,
              name: c.attrs.name,
            })),
            vat_group: {
              id: String((Number(item.id) % 3) + 1),
              name: ['Standard', 'Reduced', 'Zero'][Number(item.id) % 3],
            },
            is_external_lab_item:
              (item.attrs.type_code === 5 || item.attrs.type_code === 6) &&
              Number(item.id) % 3 === 0,
            pricing: {
              wholesale_price: randNumber({ min: 1, max: 100, fraction: 2 }),
              margin_percent: randNumber({ min: 0, max: 80, fraction: 2 }),
              price: randNumber({ min: 5, max: 200, fraction: 2 }),
              price_with_vat: randNumber({ min: 6, max: 250, fraction: 2 }),
              unit_price: randNumber({ min: 1, max: 50, fraction: 2 }),
            },
          },
        })),
        meta: {
          pagination: {
            count: totalCount,
            page: pageNumber,
            pages: Math.ceil(totalCount / pageSize),
          },
        },
      }
    },
  )

  // POST /catalog/items/v1/export/email - Email export
  server.post('/catalog/items/v1/export/email', () => {
    return { message: 'Export email sent successfully' }
  })

  // POST /catalog/items/v1/bulk/add-categories
  server.post('/catalog/items/v1/bulk/add-categories', () => {
    return { message: 'Categories added successfully' }
  })

  // POST /catalog/items/v1/bulk/remove-categories
  server.post('/catalog/items/v1/bulk/remove-categories', () => {
    return { message: 'Categories removed successfully' }
  })

  // POST /catalog/items/v1/bulk/enable-clinic-locations
  server.post('/catalog/items/v1/bulk/enable-clinic-locations', () => {
    return { message: 'Items enabled in clinic locations successfully' }
  })

  // POST /catalog/items/v1/bulk/disable-clinic-locations
  server.post('/catalog/items/v1/bulk/disable-clinic-locations', () => {
    return { message: 'Items disabled in clinic locations successfully' }
  })

  // POST /catalog/items/v1/bulk/archive
  server.post(
    '/catalog/items/v1/bulk/archive',
    function (this: MirageServer, schema, request) {
      const body = JSON.parse(request.requestBody)
      const ids: number[] = body?.data?.attributes?.ids ?? []
      for (const id of ids) {
        const item = schema.find('catalogItem', String(id))
        item?.update({ archived: true })
      }
      triggerMockItemUpdateStatus(ids)
      return { message: 'Items archived successfully' }
    },
  )

  // POST /catalog/items/v1/bulk/restore
  server.post(
    '/catalog/items/v1/bulk/restore',
    function (this: MirageServer, schema, request) {
      const body = JSON.parse(request.requestBody)
      const ids: number[] = body?.data?.attributes?.ids ?? []
      for (const id of ids) {
        const item = schema.find('catalogItem', String(id))
        item?.update({ archived: false })
      }
      triggerMockItemUpdateStatus(ids)
      return { message: 'Items restored successfully' }
    },
  )
}
